import React from 'react';
import { Clapperboard } from 'lucide-react';

/**
 * "See it work" — the walkthrough slot on the first-run dashboard.
 *
 * Two states, and the distinction is the point: with a `src` this is a real
 * player; without one it is an honest reserved slot that says a walkthrough is
 * being filmed. It never shows a play button that does nothing — a dead
 * control reads as a broken page, not a coming-soon one.
 */

export interface ExplainerVideoProps {
  /** Video URL. Absent until the walkthrough is recorded — see the note above. */
  src?: string;
  /** Still frame shown before playback; only meaningful alongside `src`. */
  poster?: string;
  /** Runtime, e.g. "2 min" — shown only when there is something to play. */
  duration?: string;
  className?: string;
}

const Frame: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="relative aspect-video w-full overflow-hidden rounded-[9px] border border-neutral-border bg-chrome">
    {children}
  </div>
);

export const ExplainerVideo: React.FC<ExplainerVideoProps> = ({
  src,
  poster,
  duration,
  className = '',
}) => (
  <section className={`rounded-card border border-line bg-surface px-[20px] py-s6 ${className}`}>
    <div className="mb-[14px] flex flex-wrap items-baseline gap-s4">
      <span className="text-card-header text-ink">See it work</span>
      <span className="text-meta text-ink-muted">
        {src
          ? `A short walkthrough of one RFQ, start to finish${duration ? ` · ${duration}` : ''}`
          : "I'm still filming this one — it'll show one RFQ from BOM to award"}
      </span>
    </div>

    <Frame>
      {src ? (
        <video
          src={src}
          poster={poster}
          controls
          preload="none"
          className="h-full w-full object-cover"
        />
      ) : (
        // Reserved slot: the same frame the video will occupy, so the page
        // does not jump when it lands. Deliberately not a play button.
        <div className="flex h-full w-full flex-col items-center justify-center gap-s3 text-center">
          <span className="flex h-[46px] w-[46px] items-center justify-center rounded-round border border-white/15 bg-white/5">
            <Clapperboard className="h-[19px] w-[19px] text-white/70" />
          </span>
          <span className="px-s6 text-[12.5px] text-white/70">Walkthrough coming soon</span>
        </div>
      )}
    </Frame>
  </section>
);

export default ExplainerVideo;
